import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import Alert from '../components/Alert';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmDialog from '../components/ConfirmDialog';
import { useForm } from '../hooks/useForm';
import { validateFilterSyntax } from '../utils/filterValidator';

const ProductOptionsManagement = () => {
    const { t, i18n } = useTranslation();
    const { isAdmin } = useAuth();
    const currentLanguage = i18n.language?.split('-')[0] || 'en'; // Get base language (e.g., 'en' from 'en-US')

    // Helper function to get translated option name
    const getTranslatedOptionName = (option) => {
        if (currentLanguage === 'en') {
            return option.optionName;
        }
        // Check if option has translations in optionNameTranslations
        if (option.optionNameTranslations && option.optionNameTranslations[currentLanguage]) {
            return option.optionNameTranslations[currentLanguage];
        }
        // Fallback to English
        return option.optionName;
    };

    // Helper function to get translated option value
    const getTranslatedValue = (optionValue) => {
        if (typeof optionValue === 'string') {
            return optionValue;
        }
        if (currentLanguage === 'en') {
            return optionValue.value;
        }
        if (optionValue.translations && optionValue.translations[currentLanguage]) {
            return optionValue.translations[currentLanguage];
        }
        return optionValue.value;
    };

    // State
    const [vendors, setVendors] = useState([]);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Selected items
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [selectedModel, setSelectedModel] = useState(null);
    const [selectedOption, setSelectedOption] = useState(null);

    // Drag and drop state
    const [draggedModel, setDraggedModel] = useState(null);
    const [dragOverModel, setDragOverModel] = useState(null);
    const [draggedOption, setDraggedOption] = useState(null);
    const [dragOverOption, setDragOverOption] = useState(null);
    const [draggedOptionValue, setDraggedOptionValue] = useState(null);
    const [dragOverOptionValue, setDragOverOptionValue] = useState(null);
    const [dragOptionContext, setDragOptionContext] = useState(null); // Which option is being reordered

    // Modal states
    const [showModelModal, setShowModelModal] = useState(false);
    const [showOptionValueModal, setShowOptionValueModal] = useState(false);
    const [showAddOptionModal, setShowAddOptionModal] = useState(false);
    const [showCopyOptionModal, setShowCopyOptionModal] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [editingFilterOption, setEditingFilterOption] = useState(null);
    const [optionToCopy, setOptionToCopy] = useState(null);

    // Confirmation dialog state
    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: null
    });

    // Filter validation state
    const [filterValidation, setFilterValidation] = useState({
        modelFilter: { isValid: true, error: null },
        optionFilter: { isValid: true, error: null }
    });
    const [validationTimers, setValidationTimers] = useState({});

    // Forms
    const filterForm = useForm({ optionFilter: '' });
    const modelForm = useForm({ name: '', modelFilter: '', vendorTableId: '' });
    const optionValueForm = useForm({
        value: '',
        translations: { nl: '', fr: '', de: '', es: '', it: '', pt: '' }
    });
    const addOptionForm = useForm({ selectedOptionId: '' });
    const copyOptionForm = useForm({ targetModelId: '' });

    const supportedLanguages = config?.supportedLanguages || ['en', 'nl', 'fr', 'de', 'es', 'it', 'pt'];

    const getModelVendorTableName = useCallback((model) => {
        if (!model) return '';

        if (model.vendorTableName) {
            return model.vendorTableName;
        }

        const matchedVendorTable = config?.vendorTables?.find(vt => vt.vendorTableID === model.vendorTableId);
        return matchedVendorTable?.vendorTableName || '';
    }, [config]);

    // Debounced filter validation
    const validateFilter = useCallback((value, filterType, fieldName) => {
        // Clear existing timer for this field
        if (validationTimers[fieldName]) {
            clearTimeout(validationTimers[fieldName]);
        }

        // Set new timer
        const timer = setTimeout(() => {
            const result = validateFilterSyntax(value, filterType);
            setFilterValidation(prev => ({
                ...prev,
                [fieldName]: result
            }));
        }, 300); // 300ms debounce

        setValidationTimers(prev => ({ ...prev, [fieldName]: timer }));
    }, [validationTimers]);

    // Cleanup timers on unmount
    useEffect(() => {
        return () => {
            Object.values(validationTimers).forEach(timer => clearTimeout(timer));
        };
    }, [validationTimers]);

    // Fetch data
    const fetchVendors = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/product-options/vendors');
            setVendors(response.data);
            setError('');
        } catch (err) {
            setError(err.response?.data?.message || t('productOptions.errors.loadFailed'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    const fetchConfig = useCallback(async () => {
        try {
            const response = await axios.get('/api/product-options/config');
            setConfig(response.data);
        } catch (err) {
            console.error('Error fetching config:', err);
        }
    }, []);

    useEffect(() => {
        fetchVendors();
        fetchConfig();
    }, [fetchVendors, fetchConfig]);

    // Update selected items when vendors change
    useEffect(() => {
        if (selectedVendor) {
            const updatedVendor = vendors.find(v => v.id === selectedVendor.id);
            if (updatedVendor) {
                setSelectedVendor(updatedVendor);
                if (selectedModel) {
                    const updatedModel = updatedVendor.models.find(m => m._id === selectedModel._id);
                    setSelectedModel(updatedModel || null);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendors, selectedVendor?.id, selectedModel?.id]);

    // Model CRUD - uses new endpoint that auto-creates vendor
    const handleCreateModel = async (e) => {
        e.preventDefault();
        setError('');

        // Validate model filter before submitting
        if (modelForm.formData.modelFilter && !filterValidation.modelFilter.isValid) {
            setError(t('productOptions.validation.filterInvalid') + ': ' + filterValidation.modelFilter.error);
            return;
        }

        try {
            // Use new endpoint that auto-creates vendor based on vendorTableId
            const response = await axios.post('/api/product-options/models', modelForm.formData);
            setSuccess(t('productOptions.success.modelCreated'));
            setShowModelModal(false);
            modelForm.resetForm();
            setFilterValidation(prev => ({ ...prev, modelFilter: { isValid: true, error: null } }));
            await fetchVendors();
            // Auto-select the created/updated vendor
            setSelectedVendor(response.data);
        } catch (err) {
            setError(err.response?.data?.message || t('productOptions.errors.createFailed'));
        }
    };

    const handleUpdateModel = async (e) => {
        e.preventDefault();
        setError('');

        // Validate model filter before submitting
        if (modelForm.formData.modelFilter && !filterValidation.modelFilter.isValid) {
            setError(t('productOptions.validation.filterInvalid') + ': ' + filterValidation.modelFilter.error);
            return;
        }

        try {
            await axios.put(
                `/api/product-options/vendors/${selectedVendor.id}/models/${editingItem._id}`,
                modelForm.formData
            );
            setSuccess(t('productOptions.success.modelUpdated'));
            setShowModelModal(false);
            setEditingItem(null);
            modelForm.resetForm();
            setFilterValidation(prev => ({ ...prev, modelFilter: { isValid: true, error: null } }));
            await fetchVendors();
        } catch (err) {
            setError(err.response?.data?.message || t('productOptions.errors.updateFailed'));
        }
    };

    const handleDeleteModel = async (model) => {
        setConfirmDialog({
            isOpen: true,
            title: t('confirmDelete.title') || 'Delete Model',
            message: t('confirmDelete.model', { name: model.name }),
            onConfirm: async () => {
                setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
                setError('');
                try {
                    const modelId = typeof model._id === 'object' ? model._id.toString() : model._id;
                    await axios.delete(`/api/product-options/vendors/${selectedVendor.id}/models/${modelId}`);
                    setSuccess(t('productOptions.success.modelDeleted'));
                    if (selectedModel?._id === model._id) {
                        setSelectedModel(null);
                        setSelectedOption(null);
                    }
                    await fetchVendors();
                } catch (err) {
                    setError(err.response?.data?.message || t('productOptions.errors.deleteFailed'));
                }
            }
        });
    };

    // Drag and Drop handlers
    const handleDragStart = (e, model) => {
        setDraggedModel(model);
        e.dataTransfer.effectAllowed = 'move';
        // Add a subtle opacity to the dragged item
        e.currentTarget.style.opacity = '0.5';
    };

    const handleDragEnd = (e) => {
        e.currentTarget.style.opacity = '1';
        setDraggedModel(null);
        setDragOverModel(null);
    };

    const handleDragOver = (e, model) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        if (draggedModel && draggedModel._id !== model._id) {
            setDragOverModel(model);
        }
    };

    const handleDragLeave = (e) => {
        // Only clear if we're leaving the container, not entering a child
        if (e.currentTarget === e.target) {
            setDragOverModel(null);
        }
    };

    const handleDrop = async (e, targetModel) => {
        e.preventDefault();

        if (!draggedModel || draggedModel._id === targetModel._id) {
            setDraggedModel(null);
            setDragOverModel(null);
            return;
        }

        setError('');
        try {
            // Get current models sorted by displayOrder
            const models = [...selectedVendor.models].sort((a, b) =>
                (a.displayOrder || 0) - (b.displayOrder || 0)
            );

            // Find indices
            const draggedIndex = models.findIndex(m => m._id === draggedModel._id);
            const targetIndex = models.findIndex(m => m._id === targetModel._id);

            if (draggedIndex === -1 || targetIndex === -1) {
                return;
            }

            // Reorder array
            const reorderedModels = [...models];
            const [removed] = reorderedModels.splice(draggedIndex, 1);
            reorderedModels.splice(targetIndex, 0, removed);

            // Create array of model IDs in new order
            const modelIds = reorderedModels.map(m => m._id);

            // Save to backend
            await axios.put(
                `/api/product-options/vendors/${selectedVendor.id}/models/reorder`,
                { modelIds }
            );

            setSuccess(t('productOptions.success.modelsReordered'));
            await fetchVendors();
        } catch (err) {
            setError(err.response?.data?.message || t('productOptions.errors.reorderFailed'));
        } finally {
            setDraggedModel(null);
            setDragOverModel(null);
        }
    };

    // Option Drag and Drop handlers
    const handleOptionDragStart = (e, option) => {
        setDraggedOption(option);
        e.dataTransfer.effectAllowed = 'move';
        e.currentTarget.style.opacity = '0.5';
    };

    const handleOptionDragEnd = (e) => {
        e.currentTarget.style.opacity = '1';
        setDraggedOption(null);
        setDragOverOption(null);
    };

    const handleOptionDragOver = (e, option) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        if (draggedOption && draggedOption._id !== option._id) {
            setDragOverOption(option);
        }
    };

    const handleOptionDragLeave = (e) => {
        if (e.currentTarget === e.target) {
            setDragOverOption(null);
        }
    };

    const handleOptionDrop = async (e, targetOption) => {
        e.preventDefault();

        if (!draggedOption || draggedOption._id === targetOption._id) {
            setDraggedOption(null);
            setDragOverOption(null);
            return;
        }

        setError('');
        try {
            // Get current options sorted by displayOrder
            const options = [...selectedModel.options].sort((a, b) =>
                (a.displayOrder || 0) - (b.displayOrder || 0)
            );

            // Find indices
            const draggedIndex = options.findIndex(o => o._id === draggedOption._id);
            const targetIndex = options.findIndex(o => o._id === targetOption._id);

            if (draggedIndex === -1 || targetIndex === -1) {
                return;
            }

            // Reorder array
            const reorderedOptions = [...options];
            const [removed] = reorderedOptions.splice(draggedIndex, 1);
            reorderedOptions.splice(targetIndex, 0, removed);

            // Create array of option IDs in new order
            const optionIds = reorderedOptions.map(o => o._id);

            // Save to backend
            await axios.put(
                `/api/product-options/vendors/${selectedVendor.id}/models/${selectedModel._id}/options/reorder`,
                { optionIds }
            );

            setSuccess(t('productOptions.success.optionsReordered'));
            await fetchVendors();
        } catch (err) {
            setError(err.response?.data?.message || t('productOptions.errors.reorderFailed'));
        } finally {
            setDraggedOption(null);
            setDragOverOption(null);
        }
    };

    // Option Value Drag and Drop handlers
    const handleValueDragStart = (e, optionValue, valueIndex, option) => {
        setDraggedOptionValue({ value: optionValue, index: valueIndex });
        setDragOptionContext(option);
        e.dataTransfer.effectAllowed = 'move';
        e.currentTarget.style.opacity = '0.5';
    };

    const handleValueDragEnd = (e) => {
        e.currentTarget.style.opacity = '1';
        setDraggedOptionValue(null);
        setDragOverOptionValue(null);
        setDragOptionContext(null);
    };

    const handleValueDragOver = (e, optionValue, valueIndex) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        if (draggedOptionValue && draggedOptionValue.index !== valueIndex) {
            setDragOverOptionValue({ value: optionValue, index: valueIndex });
        }
    };

    const handleValueDragLeave = (e) => {
        if (e.currentTarget === e.target) {
            setDragOverOptionValue(null);
        }
    };

    const handleValueDrop = async (e, targetValue, targetIndex, option) => {
        e.preventDefault();

        if (!draggedOptionValue || draggedOptionValue.index === targetIndex || !dragOptionContext || dragOptionContext._id !== option._id) {
            setDraggedOptionValue(null);
            setDragOverOptionValue(null);
            setDragOptionContext(null);
            return;
        }

        setError('');
        try {
            // Get current values sorted by displayOrder
            const values = [...option.optionValues].sort((a, b) =>
                (a.displayOrder || 0) - (b.displayOrder || 0)
            );

            // Reorder array
            const draggedIdx = draggedOptionValue.index;
            const reorderedValues = [...values];
            const [removed] = reorderedValues.splice(draggedIdx, 1);
            reorderedValues.splice(targetIndex, 0, removed);

            // Create array of value indices in new order
            // Since we're working with sorted values, we need to find original indices
            const valueIndices = reorderedValues.map(v => option.optionValues.findIndex(ov => ov.value === v.value));

            // Save to backend
            await axios.put(
                `/api/product-options/vendors/${selectedVendor.id}/models/${selectedModel._id}/options/${option._id}/values/reorder`,
                { valueIndices }
            );

            setSuccess(t('productOptions.success.valuesReordered'));
            await fetchVendors();
        } catch (err) {
            setError(err.response?.data?.message || t('productOptions.errors.reorderFailed'));
        } finally {
            setDraggedOptionValue(null);
            setDragOverOptionValue(null);
            setDragOptionContext(null);
        }
    };

    // Option CRUD
    const handleUpdateOption = async (option, updates) => {
        setError('');
        try {
            await axios.put(
                `/api/product-options/vendors/${selectedVendor.id}/models/${selectedModel._id}/options/${option._id}`,
                updates
            );
            setSuccess(t('productOptions.success.optionUpdated'));
            await fetchVendors();
        } catch (err) {
            setError(err.response?.data?.message || t('productOptions.errors.updateFailed'));
        }
    };

    // Filter modal handlers
    const openFilterModal = (option) => {
        setEditingFilterOption(option);
        filterForm.setFormData({ optionFilter: option.optionFilter || '' });
        setShowFilterModal(true);
    };

    const handleSaveFilter = async (e) => {
        e.preventDefault();
        setError('');

        // Validate option filter before submitting
        if (filterForm.formData.optionFilter && !filterValidation.optionFilter.isValid) {
            setError(t('productOptions.validation.filterInvalid') + ': ' + filterValidation.optionFilter.error);
            return;
        }

        try {
            await handleUpdateOption(editingFilterOption, {
                optionFilter: filterForm.formData.optionFilter || null
            });
            setSuccess(t('productOptions.success.filterUpdated'));
            setShowFilterModal(false);
            setEditingFilterOption(null);
            filterForm.resetForm();
            setFilterValidation(prev => ({ ...prev, optionFilter: { isValid: true, error: null } }));
        } catch (err) {
            setError(err.response?.data?.message || t('productOptions.errors.updateFailed'));
        }
    };

    const handleAddOption = async (e) => {
        e.preventDefault();
        setError('');
        try {
            const selectedAvailableOption = config.availableOptions.find(
                opt => opt.id === addOptionForm.formData.selectedOptionId
            );

            if (!selectedAvailableOption) {
                setError('Please select an option');
                return;
            }

            await axios.post(
                `/api/product-options/vendors/${selectedVendor.id}/models/${selectedModel._id}/options`,
                {
                    id: selectedAvailableOption.id,
                    optionName: selectedAvailableOption.optionName,
                    optionNameTranslations: selectedAvailableOption.optionNameTranslations || {},
                    optionFilter: null,
                    optionValues: selectedAvailableOption.defaultValues || []
                }
            );
            setSuccess(t('productOptions.success.optionAdded'));
            setShowAddOptionModal(false);
            addOptionForm.resetForm();
            await fetchVendors();
        } catch (err) {
            setError(err.response?.data?.message || t('productOptions.errors.createFailed'));
        }
    };

    const handleDeleteOption = async (option, optionIndex) => {
        const optionName = getTranslatedOptionName(option);

        setConfirmDialog({
            isOpen: true,
            title: t('confirmDelete.title') || 'Delete Option',
            message: t('confirmDelete.option', { name: optionName }),
            onConfirm: async () => {
                setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
                setError('');
                try {
                    const actualOptionIndex = selectedModel?.options?.findIndex(
                        o => o._id?.toString() === option._id?.toString()
                    );

                    if (actualOptionIndex === -1 || actualOptionIndex === undefined) {
                        setError(t('productOptions.errors.deleteFailed'));
                        return;
                    }

                    await axios.delete(
                        `/api/product-options/vendors/${selectedVendor.id}/models/${selectedModel._id}/options/${actualOptionIndex}`
                    );
                    setSuccess(t('productOptions.success.optionDeleted'));
                    await fetchVendors();
                } catch (err) {
                    setError(err.response?.data?.message || t('productOptions.errors.deleteFailed'));
                }
            }
        });
    };

    const openCopyOptionModal = (option) => {
        setOptionToCopy(option);
        copyOptionForm.resetForm();
        setShowCopyOptionModal(true);
    };

    const closeCopyOptionModal = () => {
        setShowCopyOptionModal(false);
        setOptionToCopy(null);
        copyOptionForm.resetForm();
    };

    const handleCopyOption = async (e) => {
        e.preventDefault();
        setError('');

        if (!selectedVendor || !selectedModel || !optionToCopy) {
            setError(t('productOptions.errors.createFailed'));
            return;
        }

        if (!copyOptionForm.formData.targetModelId) {
            setError(t('productOptions.copy.selectModel'));
            return;
        }

        try {
            const sourceOptionValues = Array.isArray(optionToCopy.optionValues)
                ? optionToCopy.optionValues
                : [];

            await axios.post(
                `/api/product-options/vendors/${selectedVendor.id}/models/${copyOptionForm.formData.targetModelId}/options`,
                {
                    id: optionToCopy.id,
                    optionName: optionToCopy.optionName,
                    optionNameTranslations: optionToCopy.optionNameTranslations || {},
                    optionFilter: optionToCopy.optionFilter || null,
                    optionValues: sourceOptionValues
                }
            );

            const targetModelName = selectedVendor.models?.find(
                m => m._id?.toString() === copyOptionForm.formData.targetModelId?.toString()
            )?.name || t('productOptions.copy.targetModelFallback');

            setSuccess(t('productOptions.success.optionCopied', { model: targetModelName }));
            closeCopyOptionModal();
            await fetchVendors();
        } catch (err) {
            setError(err.response?.data?.message || t('productOptions.errors.createFailed'));
        }
    };

    // Option Value CRUD
    const handleAddOptionValue = async (e) => {
        e.preventDefault();
        setError('');
        try {
            const { value, translations } = optionValueForm.formData;
            // Filter out empty translations
            const filteredTranslations = Object.fromEntries(
                Object.entries(translations).filter(([, v]) => v.trim() !== '')
            );

            await axios.post(
                `/api/product-options/vendors/${selectedVendor.id}/models/${selectedModel._id}/options/${selectedOption._id}/values`,
                { value, translations: filteredTranslations }
            );
            setSuccess(t('productOptions.success.valueAdded'));
            setShowOptionValueModal(false);
            optionValueForm.resetForm();
            await fetchVendors();
        } catch (err) {
            setError(err.response?.data?.message || t('productOptions.errors.createFailed'));
        }
    };

    const handleDeleteOptionValue = async (option, optionValue) => {
        const valueName = getTranslatedValue(optionValue);

        setConfirmDialog({
            isOpen: true,
            title: t('confirmDelete.title') || 'Delete Value',
            message: t('productOptions.confirmDelete.value', { name: valueName }) || `Are you sure you want to delete "${valueName}"?`,
            onConfirm: async () => {
                setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
                setError('');
                try {
                    // Extract the actual value string
                    const valueToDelete = typeof optionValue === 'string' ? optionValue : optionValue.value;

                    const modelId = typeof selectedModel._id === 'object' ? selectedModel._id.toString() : selectedModel._id;
                    const optionId = typeof option._id === 'object' ? option._id.toString() : option._id;

                    // Send the value itself, let server find and delete it
                    await axios.delete(
                        `/api/product-options/vendors/${selectedVendor.id}/models/${modelId}/options/${optionId}/values`,
                        { data: { value: valueToDelete } }
                    );
                    setSuccess(t('productOptions.success.valueDeleted'));
                    await fetchVendors();
                } catch (err) {
                    setError(err.response?.data?.message || t('productOptions.errors.deleteFailed'));
                }
            }
        });
    };

    // Modal handlers
    const openModelModal = (model = null) => {
        if (model) {
            setEditingItem(model);
            modelForm.setFormData({
                name: model.name,
                modelFilter: model.modelFilter || '',
                vendorTableId: model.vendorTableId || ''
            });
        } else {
            setEditingItem(null);
            modelForm.resetForm();
        }
        setShowModelModal(true);
    };

    const openOptionValueModal = (option) => {
        setSelectedOption(option);
        optionValueForm.resetForm();
        setShowOptionValueModal(true);
    };

    const openAddOptionModal = () => {
        addOptionForm.resetForm();
        setShowAddOptionModal(true);
    };

    if (!isAdmin()) {
        return (
            <div className="container mx-auto px-4 py-8">
                <Alert type="error" message={t('productOptions.errors.adminRequired')} />
            </div>
        );
    }

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-8">
                <LoadingSpinner size="lg" text={t('common.loading')} />
            </div>
        );
    }

    return (
        <div className="page-transition container mx-auto px-4 py-8">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold mb-2">{t('productOptions.title')}</h1>
                <p className="text-gray-600">{t('productOptions.description')}</p>
            </div>

            {/* Alerts */}
            <Alert type="error" message={error} onDismiss={() => setError('')} />
            <Alert type="success" message={success} onDismiss={() => setSuccess('')} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Vendors List */}
                <div className="card">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold">{t('productOptions.vendors.title')}</h2>
                    </div>
                    <div className="space-y-2">
                        {vendors.length === 0 ? (
                            <p className="text-gray-500 text-center py-4">{t('productOptions.vendors.empty')}</p>
                        ) : (
                            vendors
                                .slice()
                                .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                                .map(vendor => (
                                    <div
                                        key={vendor.id}
                                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${selectedVendor?.id === vendor.id
                                            ? 'border-blue-500 bg-blue-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                        onClick={() => {
                                            setSelectedVendor(vendor);
                                            setSelectedModel(null);
                                            setSelectedOption(null);
                                        }}
                                    >
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <p className="font-medium">{vendor.name}</p>
                                                <p className="text-sm text-gray-500">
                                                    {vendor.models?.length || 0} {t('productOptions.models.count')}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))
                        )}
                    </div>
                </div>

                {/* Models List */}
                <div className="card">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold">{t('productOptions.models.title')}</h2>
                        <button
                            onClick={() => openModelModal()}
                            className="btn-primary text-sm"
                        >
                            + {t('productOptions.models.add')}
                        </button>
                    </div>
                    {!selectedVendor ? (
                        <p className="text-gray-500 text-center py-4">{t('productOptions.models.selectVendor')}</p>
                    ) : selectedVendor.models?.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">{t('productOptions.models.empty')}</p>
                    ) : (
                        <div className="space-y-2">
                            {selectedVendor.models
                                ?.slice()
                                .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                                .map(model => (
                                    <div
                                        key={model._id}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, model)}
                                        onDragEnd={handleDragEnd}
                                        onDragOver={(e) => handleDragOver(e, model)}
                                        onDragLeave={handleDragLeave}
                                        onDrop={(e) => handleDrop(e, model)}
                                        className={`p-3 rounded-lg border cursor-move transition-all ${selectedModel?._id === model._id
                                            ? 'border-blue-500 bg-blue-50'
                                            : dragOverModel?._id === model._id
                                                ? 'border-green-500 bg-green-50'
                                                : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                        onClick={() => {
                                            setSelectedModel(model);
                                            setSelectedOption(null);
                                        }}
                                    >
                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center gap-2 flex-1">
                                                {/* Drag handle icon */}
                                                <svg
                                                    className="w-4 h-4 text-gray-400"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M4 6h16M4 12h16M4 18h16"
                                                    />
                                                </svg>
                                                <div>
                                                    <p className="font-medium">{model.name}</p>
                                                    {model.vendorTableId && (
                                                        <p className="text-xs text-gray-400">
                                                            {t('productOptions.models.linkedTo')}: {getModelVendorTableName(model)}
                                                        </p>
                                                    )}
                                                    {model.modelFilter && (
                                                        <p className="text-xs text-gray-500 mt-1 font-mono bg-gray-50 px-2 py-1 rounded inline-block">
                                                            {model.modelFilter}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openModelModal(model);
                                                    }}
                                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                                >
                                                    {t('common.edit')}
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteModel(model);
                                                    }}
                                                    className="text-red-600 hover:text-red-800 text-sm"
                                                >
                                                    {t('common.delete')}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>

                {/* Options List */}
                <div className="card">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold">{t('productOptions.options.title')}</h2>
                        {selectedModel && (
                            <button
                                onClick={openAddOptionModal}
                                className="btn-primary text-sm"
                            >
                                + {t('productOptions.options.add')}
                            </button>
                        )}
                    </div>
                    {!selectedModel ? (
                        <p className="text-gray-500 text-center py-4">{t('productOptions.options.selectModel')}</p>
                    ) : (
                        <div className="space-y-4 max-h-[600px] overflow-y-auto">
                            {selectedModel.options
                                ?.slice()
                                .sort((a, b) => {
                                    // Sort by displayOrder first
                                    const orderA = a.displayOrder !== undefined ? a.displayOrder : 999999;
                                    const orderB = b.displayOrder !== undefined ? b.displayOrder : 999999;
                                    if (orderA !== orderB) {
                                        return orderA - orderB;
                                    }
                                    // Fallback to original order field
                                    const fallbackA = a.order ?? Number.POSITIVE_INFINITY;
                                    const fallbackB = b.order ?? Number.POSITIVE_INFINITY;
                                    if (fallbackA !== fallbackB) {
                                        return fallbackA - fallbackB;
                                    }
                                    return 0;
                                })
                                .map((option, index) => (
                                    <div
                                        key={`${option._id}-${index}`}
                                        draggable
                                        onDragStart={(e) => handleOptionDragStart(e, option)}
                                        onDragEnd={handleOptionDragEnd}
                                        onDragOver={(e) => handleOptionDragOver(e, option)}
                                        onDragLeave={handleOptionDragLeave}
                                        onDrop={(e) => handleOptionDrop(e, option)}
                                        className={`border rounded-lg p-3 cursor-move transition-all ${draggedOption?._id === option._id
                                            ? 'opacity-50'
                                            : dragOverOption?._id === option._id
                                                ? 'border-2 border-blue-400 bg-blue-50'
                                                : 'border-gray-200'
                                            }`}
                                    >
                                        <div className="flex justify-between items-start mb-2">
                                            <div className="flex items-center gap-2 flex-1">
                                                {/* Drag handle icon */}
                                                <svg
                                                    className="w-4 h-4 text-gray-400 flex-shrink-0"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                >
                                                    <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
                                                </svg>
                                                <div>
                                                    <p className="font-medium">{getTranslatedOptionName(option)}</p>
                                                    {option.optionFilter && (
                                                        <p className="text-xs text-gray-500 mt-1 font-mono bg-gray-50 px-2 py-1 rounded inline-block">
                                                            {option.optionFilter}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => openFilterModal(option)}
                                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                                    title={t('productOptions.options.editFilter')}
                                                >
                                                    {t('productOptions.options.filter')}
                                                </button>
                                                <button
                                                    onClick={() => openOptionValueModal(option)}
                                                    className="text-green-600 hover:text-green-800 text-sm"
                                                >
                                                    + {t('productOptions.values.add')}
                                                </button>
                                                <button
                                                    onClick={() => openCopyOptionModal(option)}
                                                    className="text-teal-600 hover:text-teal-800 text-sm"
                                                    disabled={!selectedVendor || (selectedVendor.models?.length || 0) < 1}
                                                    title={t('productOptions.copy.button')}
                                                >
                                                    {t('productOptions.copy.button')}
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteOption(option, index)}
                                                    className="text-red-600 hover:text-red-800 text-sm"
                                                >
                                                    {t('common.delete')}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Option Values */}
                                        {option.optionValues?.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-1">
                                                {option.optionValues
                                                    .slice()
                                                    .sort((a, b) => {
                                                        // Sort by displayOrder first, then alphabetically
                                                        const orderA = a.displayOrder !== undefined ? a.displayOrder : 999999;
                                                        const orderB = b.displayOrder !== undefined ? b.displayOrder : 999999;
                                                        if (orderA !== orderB) {
                                                            return orderA - orderB;
                                                        }
                                                        const valueA = getTranslatedValue(a).toLowerCase();
                                                        const valueB = getTranslatedValue(b).toLowerCase();
                                                        return valueA.localeCompare(valueB);
                                                    })
                                                    .map((ov, valueIndex) => (
                                                        <span
                                                            key={valueIndex}
                                                            draggable
                                                            onDragStart={(e) => handleValueDragStart(e, ov, valueIndex, option)}
                                                            onDragEnd={handleValueDragEnd}
                                                            onDragOver={(e) => handleValueDragOver(e, ov, valueIndex)}
                                                            onDragLeave={handleValueDragLeave}
                                                            onDrop={(e) => handleValueDrop(e, ov, valueIndex, option)}
                                                            className={`inline-flex items-center bg-gray-100 text-gray-700 px-2 py-1 rounded text-sm cursor-move transition-all ${draggedOptionValue?.index === valueIndex && dragOptionContext?._id === option._id
                                                                ? 'opacity-50'
                                                                : dragOverOptionValue?.index === valueIndex && dragOptionContext?._id === option._id
                                                                    ? 'border-2 border-blue-400'
                                                                    : ''
                                                                }`}
                                                        >
                                                            {/* Drag handle icon */}
                                                            <svg
                                                                className="w-3 h-3 mr-1 text-gray-400"
                                                                fill="currentColor"
                                                                viewBox="0 0 20 20"
                                                            >
                                                                <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
                                                            </svg>
                                                            {getTranslatedValue(ov)}
                                                            <button
                                                                onClick={() => handleDeleteOptionValue(option, ov)}
                                                                className="ml-1 text-red-500 hover:text-red-700"
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Vendor Modal */}
            {/* Model Modal */}
            {showModelModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-xl font-semibold mb-4">
                            {editingItem ? t('productOptions.models.edit') : t('productOptions.models.create')}
                        </h3>
                        <form onSubmit={editingItem ? handleUpdateModel : handleCreateModel}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('productOptions.models.vendorTable')} *
                                    </label>
                                    <select
                                        name="vendorTableId"
                                        value={modelForm.formData.vendorTableId}
                                        onChange={modelForm.handleChange}
                                        className="input-field"
                                        required
                                    >
                                        <option value="">{t('productOptions.models.selectVendorTable')}</option>
                                        {config?.vendorTables
                                            ?.filter(vt => !selectedVendor || vt.vendorName === selectedVendor.name)
                                            .map(vt => (
                                                <option key={vt.vendorTableID} value={vt.vendorTableID}>
                                                    {vt.vendorName} - {vt.vendorType}
                                                </option>
                                            ))}
                                    </select>
                                    {modelForm.formData.vendorTableId && (
                                        <p className="text-sm text-gray-500 mt-1">
                                            {t('productOptions.models.vendorInfo')}: {
                                                config?.vendorTables?.find(vt => vt.vendorTableID === modelForm.formData.vendorTableId)?.vendorName
                                            }
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('productOptions.models.name')} *
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={modelForm.formData.name}
                                        onChange={modelForm.handleChange}
                                        className="input-field"
                                        required
                                    />
                                    {!editingItem && modelForm.formData.name && (
                                        <p className="text-sm text-gray-500 mt-1">
                                            {t('productOptions.models.generatedId')}: {
                                                modelForm.formData.name.trim().replace(/\s+/g, ' ').split(' ')
                                                    .map((w, i) => i === 0 ? w.charAt(0).toLowerCase() + w.slice(1) : w.charAt(0).toUpperCase() + w.slice(1))
                                                    .join('')
                                            }
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('productOptions.models.filter')}
                                    </label>
                                    <textarea
                                        name="modelFilter"
                                        value={modelForm.formData.modelFilter}
                                        onChange={(e) => {
                                            modelForm.handleChange(e);
                                            validateFilter(e.target.value, 'model', 'modelFilter');
                                        }}
                                        className="input-field font-mono text-sm"
                                        rows={4}
                                        placeholder="{ lengthType == 'AD' || lengthType == 'AG' }"
                                    />
                                    {/* Validation feedback */}
                                    {modelForm.formData.modelFilter && (
                                        <div className="mt-2">
                                            {filterValidation.modelFilter.isValid ? (
                                                <div className="flex items-center text-green-600 text-sm">
                                                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                    </svg>
                                                    {t('productOptions.validation.filterValid')}
                                                </div>
                                            ) : (
                                                <div className="flex items-start text-red-600 text-sm">
                                                    <svg className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                    </svg>
                                                    <span>{t('productOptions.validation.syntaxError', { error: filterValidation.modelFilter.error })}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <p className="text-xs text-gray-500 mt-1">
                                        {t('productOptions.models.filterHelp') || 'Available variables: lengthType, compressionClass, knitting, vendorTableId'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowModelModal(false);
                                        setEditingItem(null);
                                        modelForm.resetForm();
                                        setFilterValidation(prev => ({ ...prev, modelFilter: { isValid: true, error: null } }));
                                    }}
                                    className="btn-secondary"
                                >
                                    {t('common.cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="btn-primary"
                                    disabled={modelForm.formData.modelFilter && !filterValidation.modelFilter.isValid}
                                >
                                    {editingItem ? t('common.save') : t('common.confirm')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Add Option Modal */}
            {showAddOptionModal && selectedModel && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-xl font-semibold mb-4">
                            {t('productOptions.options.add')}
                        </h3>
                        <form onSubmit={handleAddOption}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('productOptions.options.selectOption')} *
                                    </label>
                                    <select
                                        name="selectedOptionId"
                                        value={addOptionForm.formData.selectedOptionId}
                                        onChange={addOptionForm.handleChange}
                                        className="input-field"
                                        required
                                    >
                                        <option value="">{t('productOptions.options.selectPlaceholder')}</option>
                                        {config?.availableOptions?.map(opt => (
                                            <option key={opt.id} value={opt.id}>
                                                {currentLanguage !== 'en' && opt.optionNameTranslations?.[currentLanguage]
                                                    ? opt.optionNameTranslations[currentLanguage]
                                                    : opt.optionName}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {t('productOptions.options.selectHelp')}
                                    </p>
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowAddOptionModal(false)}
                                    className="btn-secondary"
                                >
                                    {t('common.cancel')}
                                </button>
                                <button type="submit" className="btn-primary">
                                    {t('common.confirm')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Copy Option Modal */}
            {showCopyOptionModal && optionToCopy && selectedModel && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-xl font-semibold mb-4">
                            {t('productOptions.copy.title')}
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            {t('productOptions.copy.copying', { name: getTranslatedOptionName(optionToCopy) })}
                        </p>
                        <form onSubmit={handleCopyOption}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('productOptions.copy.selectModel')} *
                                    </label>
                                    <select
                                        name="targetModelId"
                                        value={copyOptionForm.formData.targetModelId}
                                        onChange={copyOptionForm.handleChange}
                                        className="input-field"
                                        required
                                    >
                                        <option value="">{t('productOptions.copy.selectModelPlaceholder')}</option>
                                        {selectedVendor?.models
                                            ?.slice()
                                            .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                                            .map(model => (
                                                <option key={model._id} value={model._id}>
                                                    {model.name}
                                                    {model._id?.toString() === selectedModel._id?.toString()
                                                        ? ` (${t('productOptions.copy.currentModel')})`
                                                        : ''}
                                                </option>
                                            ))}
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {t('productOptions.copy.help')}
                                    </p>
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={closeCopyOptionModal}
                                    className="btn-secondary"
                                >
                                    {t('common.cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="btn-primary"
                                    disabled={!copyOptionForm.formData.targetModelId}
                                >
                                    {t('productOptions.copy.confirm')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Option Value Modal */}
            {showOptionValueModal && selectedOption && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <h3 className="text-xl font-semibold mb-4">
                            {t('productOptions.values.addTo', { option: getTranslatedOptionName(selectedOption) })}
                        </h3>
                        <form onSubmit={handleAddOptionValue}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('productOptions.values.value')} (English) *
                                    </label>
                                    <input
                                        type="text"
                                        name="value"
                                        value={optionValueForm.formData.value}
                                        onChange={optionValueForm.handleChange}
                                        className="input-field"
                                        required
                                    />
                                </div>

                                <div className="border-t pt-4">
                                    <p className="text-sm font-medium text-gray-700 mb-3">
                                        {t('productOptions.values.translations')}
                                    </p>
                                    <div className="grid grid-cols-2 gap-3">
                                        {supportedLanguages.filter(lang => lang !== 'en').map(lang => (
                                            <div key={lang}>
                                                <label className="block text-sm text-gray-600 mb-1">
                                                    {lang.toUpperCase()}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={optionValueForm.formData.translations[lang] || ''}
                                                    onChange={(e) => {
                                                        optionValueForm.setFormData({
                                                            ...optionValueForm.formData,
                                                            translations: {
                                                                ...optionValueForm.formData.translations,
                                                                [lang]: e.target.value
                                                            }
                                                        });
                                                    }}
                                                    className="input-field text-sm"
                                                    placeholder={`Translation in ${lang.toUpperCase()}`}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowOptionValueModal(false);
                                        setSelectedOption(null);
                                    }}
                                    className="btn-secondary"
                                >
                                    {t('common.cancel')}
                                </button>
                                <button type="submit" className="btn-primary">
                                    {t('productOptions.values.add')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Filter Edit Modal */}
            {showFilterModal && editingFilterOption && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-xl font-semibold mb-4">
                            {t('productOptions.options.editFilter')}
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            {t('productOptions.options.editingFilterFor')}: <span className="font-medium">{getTranslatedOptionName(editingFilterOption)}</span>
                        </p>
                        <form onSubmit={handleSaveFilter}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('productOptions.options.filter')}
                                    </label>
                                    <textarea
                                        name="optionFilter"
                                        value={filterForm.formData.optionFilter}
                                        onChange={(e) => {
                                            filterForm.handleChange(e);
                                            validateFilter(e.target.value, 'option', 'optionFilter');
                                        }}
                                        className="input-field font-mono text-sm"
                                        rows={4}
                                        placeholder="e.g., lengthType == 'AD' || lengthType == 'AG'"
                                    />
                                    {/* Validation feedback */}
                                    {filterForm.formData.optionFilter && (
                                        <div className="mt-2">
                                            {filterValidation.optionFilter.isValid ? (
                                                <div className="flex items-center text-green-600 text-sm">
                                                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                    </svg>
                                                    {t('productOptions.validation.filterValid')}
                                                </div>
                                            ) : (
                                                <div className="flex items-start text-red-600 text-sm">
                                                    <svg className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                    </svg>
                                                    <span>{t('productOptions.validation.syntaxError', { error: filterValidation.optionFilter.error })}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <p className="text-xs text-gray-500 mt-1">
                                        {t('productOptions.options.filterHelp')}
                                    </p>
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowFilterModal(false);
                                        setEditingFilterOption(null);
                                        filterForm.resetForm();
                                        setFilterValidation(prev => ({ ...prev, optionFilter: { isValid: true, error: null } }));
                                    }}
                                    className="btn-secondary"
                                >
                                    {t('common.cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="btn-primary"
                                    disabled={filterForm.formData.optionFilter && !filterValidation.optionFilter.isValid}
                                >
                                    {t('common.save')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Confirmation Dialog */}
            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={confirmDialog.onConfirm}
                onCancel={() => setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null })}
                confirmText={t('common.delete') || 'Delete'}
                cancelText={t('common.cancel') || 'Cancel'}
            />
        </div>
    );
};

export default ProductOptionsManagement;
